// Backend: GET /api/farms  GET /api/branches

export type FarmStatus = 'active' | 'inactive' | 'suspended';
export type FarmType = 'broiler' | 'layer' | 'breeder' | 'indigenous' | 'mixed';
export type BranchStatus = 'active' | 'inactive';

export interface Branch {
  id: string;
  branchCode: string;
  branchName: string;
  farmId: string;
  manager: string;
  managerPhone: string;
  country: string;
  district: string;
  subcounty: string;
  village: string;
  gpsLat: string;
  gpsLng: string;
  phone: string;
  email: string;
  openingDate: string;
  capacity: number;
  currentBirds: number;
  activeHouses: number;
  activeFlocks: number;
  status: BranchStatus;
  notes: string;
}

export interface Farm {
  id: string;
  farmCode: string;
  farmName: string;
  farmType: FarmType;
  owner: string;
  manager: string;
  managerPhone: string;
  managerEmail: string;
  country: string;
  district: string;
  subcounty: string;
  village: string;
  gpsLat: string;
  gpsLng: string;
  phone: string;
  email: string;
  openingDate: string;
  totalCapacity: number;
  currentBirds: number;
  activeHouses: number;
  activeFlocks: number;
  totalEmployees: number;
  status: FarmStatus;
  branches: Branch[];
  // Performance snapshot
  monthlyRevenue: number;
  monthlyExpenses: number;
  eggsTodayCount: number;
  mortalityToday: number;
  feedInStock: number; // kg
}

export const MOCK_FARMS: Farm[] = [
  {
    id: 'farm-001',
    farmCode: 'IDF-001',
    farmName: 'Idudi Poultry Farm',
    farmType: 'mixed',
    owner: 'Mr. John Okello',
    manager: 'Sarah Namukasa',
    managerPhone: '+256 772 100 200',
    managerEmail: 'sarah.namukasa@y77poultry.com',
    country: 'Uganda',
    district: 'Iganga',
    subcounty: 'Idudi',
    village: 'Nabitende',
    gpsLat: '0.4521',
    gpsLng: '33.4812',
    phone: '+256 414 100 200',
    email: 'idudi@y77poultry.com',
    openingDate: '2019-03-15',
    totalCapacity: 25000,
    currentBirds: 18420,
    activeHouses: 6,
    activeFlocks: 4,
    totalEmployees: 18,
    status: 'active',
    monthlyRevenue: 48500000,
    monthlyExpenses: 31200000,
    eggsTodayCount: 3840,
    mortalityToday: 12,
    feedInStock: 4200,
    branches: [
      {
        id: 'branch-001',
        branchCode: 'IDF-B01',
        branchName: 'Idudi Main Site',
        farmId: 'farm-001',
        manager: 'Sarah Namukasa',
        managerPhone: '+256 772 100 200',
        country: 'Uganda',
        district: 'Iganga',
        subcounty: 'Idudi',
        village: 'Nabitende',
        gpsLat: '0.4521',
        gpsLng: '33.4812',
        phone: '+256 414 100 200',
        email: 'idudi.main@y77poultry.com',
        openingDate: '2019-03-15',
        capacity: 15000,
        currentBirds: 11200,
        activeHouses: 4,
        activeFlocks: 3,
        status: 'active',
        notes: 'Primary broiler and layer production site.',
      },
      {
        id: 'branch-002',
        branchCode: 'IDF-B02',
        branchName: 'Idudi Extension Site',
        farmId: 'farm-001',
        manager: 'Peter Waiswa',
        managerPhone: '+256 701 300 400',
        country: 'Uganda',
        district: 'Iganga',
        subcounty: 'Idudi',
        village: 'Bulamagi',
        gpsLat: '0.4610',
        gpsLng: '33.4950',
        phone: '+256 414 100 201',
        email: 'idudi.ext@y77poultry.com',
        openingDate: '2021-06-01',
        capacity: 10000,
        currentBirds: 7220,
        activeHouses: 2,
        activeFlocks: 1,
        status: 'active',
        notes: 'Breeder and indigenous bird extension.',
      },
    ],
  },
  {
    id: 'farm-002',
    farmCode: 'KDF-001',
    farmName: 'Kampala Demonstration Farm',
    farmType: 'layer',
    owner: 'Y77 Technologies Limited',
    manager: 'Dr. Grace Atim',
    managerPhone: '+256 782 500 600',
    managerEmail: 'grace.atim@y77poultry.com',
    country: 'Uganda',
    district: 'Wakiso',
    subcounty: 'Nansana',
    village: 'Kawanda',
    gpsLat: '0.3990',
    gpsLng: '32.5280',
    phone: '+256 414 200 300',
    email: 'kampala@y77poultry.com',
    openingDate: '2022-01-10',
    totalCapacity: 12000,
    currentBirds: 9850,
    activeHouses: 3,
    activeFlocks: 2,
    totalEmployees: 11,
    status: 'active',
    monthlyRevenue: 27800000,
    monthlyExpenses: 18400000,
    eggsTodayCount: 5210,
    mortalityToday: 5,
    feedInStock: 2800,
    branches: [
      {
        id: 'branch-003',
        branchCode: 'KDF-B01',
        branchName: 'Kawanda Layer Unit',
        farmId: 'farm-002',
        manager: 'Dr. Grace Atim',
        managerPhone: '+256 782 500 600',
        country: 'Uganda',
        district: 'Wakiso',
        subcounty: 'Nansana',
        village: 'Kawanda',
        gpsLat: '0.3990',
        gpsLng: '32.5280',
        phone: '+256 414 200 300',
        email: 'kawanda@y77poultry.com',
        openingDate: '2022-01-10',
        capacity: 12000,
        currentBirds: 9850,
        activeHouses: 3,
        activeFlocks: 2,
        status: 'active',
        notes: 'High-density layer production and demonstration unit.',
      },
    ],
  },
  {
    id: 'farm-003',
    farmCode: 'MBF-001',
    farmName: 'Mbarara Broiler Farm',
    farmType: 'broiler',
    owner: 'Mr. Robert Tumusiime',
    manager: 'Alice Kyomugisha',
    managerPhone: '+256 756 700 800',
    managerEmail: 'alice.k@y77poultry.com',
    country: 'Uganda',
    district: 'Mbarara',
    subcounty: 'Kakoba',
    village: 'Rutooma',
    gpsLat: '-0.6070',
    gpsLng: '30.6545',
    phone: '+256 414 300 400',
    email: 'mbarara@y77poultry.com',
    openingDate: '2020-08-20',
    totalCapacity: 8000,
    currentBirds: 0,
    activeHouses: 2,
    activeFlocks: 0,
    totalEmployees: 7,
    status: 'inactive',
    monthlyRevenue: 0,
    monthlyExpenses: 1200000,
    eggsTodayCount: 0,
    mortalityToday: 0,
    feedInStock: 0,
    branches: [],
  },
];

export const FARM_TYPE_LABELS: Record<FarmType, string> = {
  broiler: 'Broiler',
  layer: 'Layer',
  breeder: 'Breeder',
  indigenous: 'Indigenous',
  mixed: 'Mixed',
};

export const FARM_TYPE_COLORS: Record<FarmType, string> = {
  broiler: 'bg-orange-100 text-orange-700',
  layer: 'bg-yellow-100 text-yellow-700',
  breeder: 'bg-purple-100 text-purple-700',
  indigenous: 'bg-teal-100 text-teal-700',
  mixed: 'bg-blue-100 text-blue-700',
};

export const STATUS_COLORS: Record<FarmStatus | BranchStatus, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
  suspended: 'bg-red-100 text-red-700',
};

// Downstream scope context — used by other modules to scope operations
export interface FarmScopeContext {
  farmId: string | null;
  branchId: string | null;
  farmName: string;
  branchName: string;
}

export const DEFAULT_SCOPE: FarmScopeContext = {
  farmId: null,
  branchId: null,
  farmName: 'All Farms',
  branchName: 'All Branches',
};
